/**
 * cashOffer — the "+ Add cash" side of a trade, on the client.
 *
 * Mirrors server/trades/cashTerms.js. Cash here is a TERM OF AN AGREEMENT, not a
 * payment: Tradeloft never holds or moves money. The two traders settle it
 * themselves — in person, or through an app they already use (Bit, PayPal) — so
 * the copy must never imply the app processed, escrowed or verified anything.
 *
 * Kept pure so the rules that matter (what a valid amount is, when cash may join
 * a fairness total, and what we say when it may not) are unit-testable without
 * rendering a modal.
 */
import { formatMoney, currencySymbol, parseMoneyInput } from "../utils/currency";

export { currencySymbol };

/** Must match SUPPORTED_CASH_CURRENCIES on the server. */
export const CASH_CURRENCIES = ["ILS", "EUR", "USD"];

/**
 * How the pair will settle. Tradeloft only records the choice and hands off —
 * it never processes any of these.
 */
export const SETTLEMENT_METHODS = [
  { id: "cash_in_person", label: "Cash in person" },
  { id: "bit", label: "Bit" },
  { id: "paypal", label: "PayPal" },
  { id: "other", label: "Other" },
];

export const settlementLabel = (id) =>
  SETTLEMENT_METHODS.find((m) => m.id === id)?.label || "Cash in person";

/** Must match MAX_CASH_AMOUNT on the server. */
export const MAX_CASH_AMOUNT = 100000;

/**
 * Parse what the user typed into the cash field.
 *
 * Accepts a plain decimal; refuses anything the server would refuse, so the
 * rejection happens under the user's thumb rather than after a round trip.
 * An empty field is not an error — it just means "no cash".
 *
 * [B-22] The decimal itself is read by `parseMoneyInput`, the parser the asking
 * price uses too, so "50,25" is 50.25 here as well instead of a refusal.
 */
export function parseCashInput(text, currency = "ILS") {
  const raw = String(text ?? "").trim();
  if (raw === "") return { empty: true, cash: null, error: null };

  // Reject anything that is not a simple decimal: catches "1e9", "abc",
  // "1,500", "-5", and stray currency symbols before they reach Number().
  const amount = parseMoneyInput(raw);
  if (amount === null) {
    return { empty: false, cash: null, error: "Enter an amount like 50 or 50.25" };
  }

  if (!Number.isFinite(amount) || amount <= 0) {
    return { empty: false, cash: null, error: "Enter an amount greater than zero" };
  }
  if (amount > MAX_CASH_AMOUNT) {
    return { empty: false, cash: null, error: `Keep it under ${MAX_CASH_AMOUNT.toLocaleString()}` };
  }
  if (!CASH_CURRENCIES.includes(currency)) {
    return { empty: false, cash: null, error: "Choose a supported currency" };
  }

  return { empty: false, error: null, cash: { amount, currency } };
}

/**
 * "₪50.00" — the amount as promised, in the currency promised.
 *
 * Formatted through the shared currency boundary rather than a local symbol
 * map: symbols live in exactly one place so no screen can ever pair a figure
 * with the wrong currency's sign, which is the bug #64 exists to prevent.
 */
export function formatCash(cash) {
  if (!cash) return "";
  return formatMoney(Number(cash.amount), cash.currency);
}

/** Chip copy for an attached cash term. */
export const cashChipLabel = (cash) => (cash ? `+ ${formatCash(cash)} cash` : "");

/**
 * Can this cash join a fairness total denominated in `baseCurrency`?
 *
 * Only when it is already in that currency, or a real rate snapshot exists. With
 * no approved FX source there is no honest way to express ₪50 as euros, and
 * guessing would put a number on screen we cannot stand behind.
 */
export function canIncludeInTotal(cash, baseCurrency = "EUR") {
  if (!cash) return true;
  if (String(cash.currency).toUpperCase() === String(baseCurrency).toUpperCase()) return true;
  const converted = Number(cash?.fxSnapshot?.convertedAmount);
  return Number.isFinite(converted) && converted > 0;
}

/**
 * One side's total for the fairness meter, with its cash folded in only when
 * that can be done honestly.
 */
export function sideTotalWithCash({ cardsTotal = 0, cash = null, baseCurrency = "EUR" } = {}) {
  if (!cash) return { total: cardsTotal, cardsTotal, cash: null, cashIncluded: true };
  if (!canIncludeInTotal(cash, baseCurrency)) {
    return { total: cardsTotal, cardsTotal, cash, cashIncluded: false };
  }
  const add =
    String(cash.currency).toUpperCase() === String(baseCurrency).toUpperCase()
      ? Number(cash.amount)
      : Number(cash.fxSnapshot.convertedAmount);
  return { total: cardsTotal + add, cardsTotal, cash, cashIncluded: true };
}

/**
 * What to tell the user about cash in the fairness estimate.
 *
 * When the cash cannot be converted we say so plainly — the agreement still
 * stands, it just is not part of the estimate. Silence would read as "counted".
 */
export function cashFairnessNote({ cash, cashIncluded, baseCurrency = "EUR" }) {
  if (!cash) return null;
  if (cashIncluded) return null;
  return `${formatCash(cash)} isn’t included in the fairness estimate — we can’t convert ${cash.currency} to ${baseCurrency} yet. The agreed amount is unchanged.`;
}

/**
 * The reminder that has to sit next to any cash term: this app records the
 * agreement, it does not move the money.
 */
export const CASH_DISCLAIMER =
  "Cash is exchanged directly between you two. Tradeloft doesn’t process, hold or verify it.";
